/**
 * Детали одного пользователя: профиль + последние события + чат-сессии.
 */

import { Alert, Badge, Card, Center, Group, Loader, Stack, Table, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { fetchAdminUser } from '../../lib/api-admin';
import { fmtDate, fmtRelative } from './format';

export function AdminUserDetailPage(): JSX.Element {
    const params = useParams();
    const email = decodeURIComponent(params.email ?? '');
    const q = useQuery({
        queryKey: ['admin-user', email],
        queryFn:  () => fetchAdminUser(email),
        retry: false,
        enabled: Boolean(email),
    });

    return (
        <Stack gap="md">
            <Group justify="space-between">
                <Title order={2}>{email}</Title>
                <Link to="/admin/users"><Text fz="sm">← к списку</Text></Link>
            </Group>
            {q.isLoading && <Center my="lg"><Loader /></Center>}
            {q.error && <Alert color="red">{(q.error as Error).message}</Alert>}
            {q.data && (
                <Stack gap="lg">
                    <Card withBorder padding="md">
                        <Group justify="space-between" wrap="wrap">
                            <Group gap="md">
                                <Text>{q.data.user.name ?? '—'}</Text>
                                <Badge color={q.data.user.role === 'admin' ? 'indigo' : 'gray'} variant="light">{q.data.user.role}</Badge>
                            </Group>
                            <Group gap="md" fz="sm" c="dimmed">
                                <Text fz="sm">первый вход {fmtDate(q.data.user.firstSeen)}</Text>
                                <Text fz="sm">последний {fmtRelative(q.data.user.lastSeen)}</Text>
                            </Group>
                        </Group>
                    </Card>

                    <div>
                        <Title order={4} mb="xs">Чат-сессии ({q.data.sessions.length})</Title>
                        {q.data.sessions.length === 0
                            ? <Text c="dimmed" fz="sm">пока нет</Text>
                            : (
                                <Card withBorder padding={0}>
                                    <Table fz="sm" striped verticalSpacing="xs">
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Заголовок</Table.Th>
                                                <Table.Th>Репо</Table.Th>
                                                <Table.Th>Сообщений</Table.Th>
                                                <Table.Th>Обновлено</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {q.data.sessions.map(s => (
                                                <Table.Tr key={s.id}>
                                                    <Table.Td>
                                                        <Link to={`/admin/chats/${encodeURIComponent(s.id)}`}>{s.title || '(без заголовка)'}</Link>
                                                    </Table.Td>
                                                    <Table.Td>{s.owner}/{s.repo}</Table.Td>
                                                    <Table.Td>{s.messageCount}</Table.Td>
                                                    <Table.Td><Text fz="xs">{fmtRelative(s.updatedAt)}</Text></Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </Card>
                            )
                        }
                    </div>

                    <div>
                        <Title order={4} mb="xs">События ({q.data.events.length})</Title>
                        {q.data.events.length === 0
                            ? <Text c="dimmed" fz="sm">ничего не зафиксировано</Text>
                            : (
                                <Card withBorder padding={0}>
                                    <Table fz="sm" striped verticalSpacing="xs">
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Когда</Table.Th>
                                                <Table.Th>Тип</Table.Th>
                                                <Table.Th>Где</Table.Th>
                                                <Table.Th>Детали</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {q.data.events.map(e => (
                                                <Table.Tr key={e.id}>
                                                    <Table.Td><Text fz="xs" ff="monospace">{fmtDate(e.ts)}</Text></Table.Td>
                                                    <Table.Td><Badge size="sm" variant="light">{e.kind}</Badge></Table.Td>
                                                    <Table.Td><Text fz="xs">{e.owner ? `${e.owner}/${e.repo ?? ''}` : '—'}</Text></Table.Td>
                                                    <Table.Td><Text fz="xs" ff="monospace" style={{ wordBreak: 'break-all' }}>
                                                        {e.meta ? JSON.stringify(e.meta) : ''}
                                                    </Text></Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </Card>
                            )
                        }
                    </div>
                </Stack>
            )}
        </Stack>
    );
}
